import React, { useState, useMemo } from 'react';
import {
  Phone,
  Mail,
  User,
  Send,
  Plus,
  TrendingUp,
  Eye,
  Activity,
  Clock,
  FileText,
  CheckCircle2,
  Package,
  Users,
  CreditCard,
  AlertCircle
} from 'lucide-react';
import { RequestProjectExpenseModal } from '../modals/RequestProjectExpenseModal';
import { RecordProjectCreditModal } from '../modals/RecordProjectCreditModal';
import { ProjectTransactionHistoryModal } from '../modals/ProjectTransactionHistoryModal';

const financeRoles = ['owner', 'admin', 'designer'];

const ACTIVITY_CONFIG = {
  created: { icon: Plus, color: 'text-blue-500', bg: 'bg-blue-50' },
  note: { icon: FileText, color: 'text-slate-500', bg: 'bg-slate-50' },
  pm_assigned: { icon: User, color: 'text-[#2b448f]', bg: 'bg-blue-50' },
  team_updated: { icon: Users, color: 'text-blue-500', bg: 'bg-blue-50' },
  task_created: { icon: CheckCircle2, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  material_request: { icon: Package, color: 'text-amber-500', bg: 'bg-amber-50' },
  material_receipt: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  work_update: { icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' },
  status_change: { icon: Activity, color: 'text-purple-500', bg: 'bg-purple-50' },
  payment_credit: { icon: CreditCard, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  expense_request: { icon: Send, color: 'text-rose-500', bg: 'bg-rose-50' },
  documents_uploaded: { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
};

export const ProjectSideStats = ({ project, formatCurrency, onProjectUpdated, viewerRole }) => {
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const canManageFinance = financeRoles.includes(viewerRole || '');

  const ps = useMemo(() => {
    const p = project?.paymentSummary;
    const contractValue = p?.contractValue || project?.projectValue || project?.leadId?.quotationAmount || project?.leadId?.budget || project?.quotationAmount || project?.budget || project?.targetCost || 0;
    const targetCost = p?.targetCost ?? project?.targetCost ?? 0;
    const civilTargetCost = project?.civilTargetCost ?? 0;
    const electricalTargetCost = project?.electricalTargetCost ?? 0;
    const mechanicalTargetCost = project?.mechanicalTargetCost ?? 0;
    const civilExpenseLimit = project?.civilExpenseLimit ?? 0;
    const electricalExpenseLimit = project?.electricalExpenseLimit ?? 0;
    const mechanicalExpenseLimit = project?.mechanicalExpenseLimit ?? 0;
    const advancePaid = p?.advancePaid ?? project?.advancePaid ?? 0;
    const totalCredits = p?.totalCredits ?? 0;
    const totalExpenses = p?.totalExpenses ?? 0;
    const epAmount = p?.epAmount ?? 0;
    const nonStandardAmount = p?.nonStandardAmount ?? 0;
    const othersAmount = p?.othersAmount ?? 0;
    const civilStandardAmount = p?.civilStandardAmount ?? 0;
    const electricalStandardAmount = p?.electricalStandardAmount ?? 0;
    const mechanicalStandardAmount = p?.mechanicalStandardAmount ?? 0;
    const civilNonStandardAmount = p?.civilNonStandardAmount ?? 0;
    const electricalNonStandardAmount = p?.electricalNonStandardAmount ?? 0;
    const mechanicalNonStandardAmount = p?.mechanicalNonStandardAmount ?? 0;
    const civilOthersAmount = p?.civilOthersAmount ?? 0;
    const electricalOthersAmount = p?.electricalOthersAmount ?? 0;
    const mechanicalOthersAmount = p?.mechanicalOthersAmount ?? 0;
    const civilNotApplicableAmount = p?.civilNotApplicableAmount ?? 0;
    const electricalNotApplicableAmount = p?.electricalNotApplicableAmount ?? 0;
    const mechanicalNotApplicableAmount = p?.mechanicalNotApplicableAmount ?? 0;
    const civilExpenses = p?.civilExpenses ?? 0;
    const electricalExpenses = p?.electricalExpenses ?? 0;
    const mechanicalExpenses = p?.mechanicalExpenses ?? 0;
    const civilMaterialCost = p?.civilMaterialCost ?? 0;
    const electricalMaterialCost = p?.electricalMaterialCost ?? 0;
    const mechanicalMaterialCost = p?.mechanicalMaterialCost ?? 0;
    const pendingProjectExpenseTotal = p?.pendingProjectExpenseTotal ?? 0;
    const totalMaterialCost = p?.totalMaterialCost ?? 0;
    const totalPaid = p?.totalPaid ?? p?.totalReceived ?? advancePaid + totalCredits;
    const netBalance = contractValue - totalPaid;
    const totalExpensesSum = totalMaterialCost + totalExpenses;
    const profitOrLoss = totalExpensesSum === 0 ? 0 : Math.abs(totalExpensesSum - contractValue);
    const collectionPercent =
      p?.collectionPercent ??
      (contractValue > 0 ? Math.min(100, Math.round((totalPaid / contractValue) * 100)) : 0);
    const budgetValue = targetCost - totalExpenses;
    const isBudgetExceeded = budgetValue < 0;
    const budgetRemaining = p?.budgetRemaining ?? Math.max(0, budgetValue);

    const isPoComplete = totalPaid >= contractValue;
    const totalRequired = contractValue + nonStandardAmount;
    const remainingNonStd = Math.max(0, totalRequired - Math.max(contractValue, totalPaid));
    const showNonStdWarning = isPoComplete && remainingNonStd > 0;

    return {
      contractValue,
      targetCost,
      civilTargetCost,
      electricalTargetCost,
      mechanicalTargetCost,
      civilExpenseLimit,
      electricalExpenseLimit,
      mechanicalExpenseLimit,
      civilStandardAmount,
      electricalStandardAmount,
      mechanicalStandardAmount,
      civilNonStandardAmount,
      electricalNonStandardAmount,
      mechanicalNonStandardAmount,
      civilOthersAmount,
      electricalOthersAmount,
      mechanicalOthersAmount,
      civilNotApplicableAmount,
      electricalNotApplicableAmount,
      mechanicalNotApplicableAmount,
      civilExpenses,
      electricalExpenses,
      mechanicalExpenses,
      civilMaterialCost,
      electricalMaterialCost,
      mechanicalMaterialCost,
      totalMaterialCost,
      totalPaid,
      netBalance,
      profitOrLoss,
      totalExpenses,
      epAmount,
      nonStandardAmount,
      othersAmount,
      pendingProjectExpenseTotal,
      budgetRemaining,
      collectionPercent,
      isBudgetExceeded,
      budgetValue,
      isPoComplete,
      remainingNonStd,
      showNonStdWarning
    };
  }, [project]);

  const ledgerRows = useMemo(() => {
    const txs = project?.paymentSummary?.transactions;
    if (!Array.isArray(txs) || txs.length === 0) return [];
    const isGen = (t) => t?.expenseCategory === 'general' || t?.relatedToProject === false;
    const out = [];
    for (const t of txs) {
      if (t?.kind === 'credit') {
        if (t.openingAdvance) continue;
        out.push({
          id: t._id,
          kind: 'credit',
          label: t.paymentType || 'Credit in',
          amount: Number(t.amount) || 0,
          date: t.transactionDate || t.createdAt,
          status: 'Recorded',
          statusClass: 'text-emerald-600/90'
        });
        continue;
      }
      if (t?.kind === 'expense' && !isGen(t)) {
        const st = t.requestStatus || 'pending';
        const stLabel =
          st === 'pending'
            ? 'Pending approval'
            : st === 'rejected'
              ? 'Rejected'
              : st === 'partial_approved'
                ? 'Partially approved'
                : st === 'paid'
                  ? 'Paid out'
                  : st === 'approved'
                    ? 'Approved'
                    : st;
        const stClass =
          st === 'pending'
            ? 'text-amber-600'
            : st === 'rejected'
              ? 'text-rose-600'
              : 'text-slate-500';
        const showAmt =
          st === 'pending' || st === 'rejected'
            ? Number(t.amount) || 0
            : t.approvedAmount != null && !Number.isNaN(Number(t.approvedAmount))
              ? Number(t.approvedAmount)
              : Number(t.amount) || 0;
        out.push({
          id: t._id,
          kind: 'expense',
          label: t.expenseReason || t.categoryLabel || 'Project expense',
          amount: showAmt,
          date: t.transactionDate || t.createdAt,
          status: stLabel,
          statusClass: stClass
        });
      }
    }
    out.sort(
      (a, b) =>
        new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
    );
    return out;
  }, [project?.paymentSummary?.transactions]);

  const activityRows = useMemo(() => {
    const logs = project?.activityLog || [];
    return [...logs]
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
      .slice(0, 5);
  }, [project?.activityLog]);

  const getInitials = (name) => {
    if (!name) return 'PM';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const handleAfterMutation = async () => {
    if (onProjectUpdated) await onProjectUpdated(true);
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <div className="w-full h-full flex flex-col gap-4 shrink-0">
      {viewerRole !== 'installation_team' && (
        <div className="rounded-xl shadow-sm animate-in fade-in slide-in-from-right duration-500 border border-slate-200/80 overflow-hidden">
          {/* Top Dark Blue Section */}
          <div className="bg-[#1e3a8a] text-white p-4 sm:p-5 relative overflow-hidden">
            <div className="absolute -right-2 -bottom-4 opacity-10 select-none pointer-events-none">
              <span className="text-[100px] font-black italic leading-none">₹</span>
            </div>

            <div className="relative z-10 flex items-center justify-between gap-2 mb-4">
              <span className="text-[12px] sm:text-[13px] font-bold tracking-wider uppercase font-poppins text-white">
                Payment Summary
              </span>
              {/* Developer : Nithin
                 Date : 14-07-2026
                 changes: Commented out Send and Plus action icons in Payment Summary card header as requested
              {canManageFinance && (
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    id="payment-summary-expense"
                    title="Request project expense"
                    onClick={() => setExpenseOpen(true)}
                    className="h-8 w-8 flex items-center justify-center rounded-lg border border-white/20 bg-white/10 text-white hover:bg-white/20 transition-all duration-200"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    id="payment-summary-credit"
                    title="View Transaction History"
                    onClick={() => setHistoryOpen(true)}
                    className="h-8 w-8 flex cursor-pointer items-center justify-center rounded-lg border border-white/20 bg-white/10 text-white hover:bg-white/20 transition-all duration-200"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              )}
              */}
            </div>

            <div className="relative z-10 grid grid-cols-2 gap-3">
              <div className="flex flex-col">
                <span className="text-blue-200/80 text-[10px] font-semibold tracking-wider uppercase font-poppins block">
                  Project value (₹)
                </span>
                <span className="text-white text-lg sm:text-xl font-extrabold font-poppins tracking-tight mt-0.5 block">
                  {formatCurrency(ps.contractValue)}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-blue-200/80 text-[10px] font-semibold tracking-wider uppercase font-poppins block">
                  Amount received (₹)
                </span>
                <span className="text-white text-lg sm:text-xl font-extrabold font-poppins tracking-tight mt-0.5 flex items-center gap-1">
                  <span className="text-blue-300/80 font-normal">₹</span> {formatCurrency(ps.totalPaid)}
                </span>
              </div>
            </div>
          </div>

          {/* Bottom Crisp White Section */}
          <div className="bg-white p-4 sm:p-5 flex flex-col">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col">
                <span className="text-slate-400 text-[10px] font-semibold tracking-wider uppercase font-poppins block" title="Project Value - Amount Received">
                  PENDING BALANCE (₹) <span className="text-[9px] font-normal text-slate-400"></span>
                </span>
                <span className="text-[#1e3a8a] text-lg sm:text-xl font-extrabold font-poppins mt-0.5 block">
                  {ps.netBalance < 0 ? '-' : ''}{formatCurrency(Math.abs(ps.netBalance))}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-slate-400 text-[10px] font-semibold tracking-wider uppercase font-poppins block" title="Material Expenses + Project Expenses - Project Value">
                  PROFIT/LOSS (₹) <span className="text-[9px] font-normal text-slate-400">(ME+PE - PO)</span>
                </span>
                <span className="text-slate-800 text-lg sm:text-xl font-extrabold font-poppins mt-0.5 block">
                  {formatCurrency(Math.abs(ps.profitOrLoss))}
                </span>
              </div>
            </div>

            <hr className="my-4 border-slate-100" />

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col">
                <span className="text-slate-400 text-[10px] font-semibold tracking-wider uppercase font-poppins block">
                  Material Expenses (₹)
                </span>
                <span className="text-slate-800 text-lg sm:text-xl font-extrabold font-poppins mt-0.5 block">
                  {formatCurrency(ps.totalMaterialCost)}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-slate-400 text-[10px] font-semibold tracking-wider uppercase font-poppins flex items-center gap-1">
                  Project expenses (₹)
                  <span className="w-3.5 h-3.5 rounded-full border border-slate-300 text-slate-400 flex items-center justify-center text-[9px] font-bold cursor-help" title="Total project expenses incurred">i</span>
                </span>
                <span className="text-slate-800 text-lg sm:text-xl font-extrabold font-poppins mt-0.5 block">
                  {formatCurrency(ps.totalExpenses)}
                </span>
              </div>
            </div>

            {/* Payment Type Breakdown if applicable */}
            {(ps.civilTargetCost > 0 || ps.electricalTargetCost > 0 || ps.mechanicalTargetCost > 0) && (
              <div className="mt-4 pt-3.5 border-t border-slate-100 flex flex-col gap-2">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Payment Type Breakdown</span>
                <div className="bg-slate-50 rounded-lg border border-slate-200/80 overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-max">
                    <thead className="bg-slate-100/80 border-b border-slate-200/60">
                      <tr>
                        <th className="px-2.5 py-1.5 text-[11px] text-slate-600 font-extrabold uppercase">Role</th>
                        <th className="px-2.5 py-1.5 text-[11px] text-slate-600 font-extrabold uppercase text-right">Budget / Spent</th>
                        <th className="px-2.5 py-1.5 text-[11px] text-slate-600 font-extrabold uppercase text-right">Limit / Exp. Spent</th>
                        <th className="px-2.5 py-1.5 text-[11px] text-slate-600 font-extrabold uppercase text-right">Material Cost</th>
                        <th className="px-2.5 py-1.5 text-[11px] text-slate-600 font-extrabold uppercase text-right">Standard</th>
                        <th className="px-2.5 py-1.5 text-[11px] text-slate-600 font-extrabold uppercase text-right">Non Std</th>
                        <th className="px-2.5 py-1.5 text-[11px] text-slate-600 font-extrabold uppercase text-right">Others</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200/60 text-slate-700">
                      {/* Civil Row */}
                      <tr className="hover:bg-slate-100/50 transition-colors">
                        <td className="px-2.5 py-1.5 text-[12px] font-semibold text-slate-800 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                            Civil
                          </div>
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-medium">
                          ₹{formatCurrency(ps.civilTargetCost)} <span className="text-slate-300 mx-0.5">/</span> <span className="text-slate-800 font-bold">₹{formatCurrency(Math.max(0, (ps.civilExpenses || 0) - (ps.civilNonStandardAmount || 0)))}</span>
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-medium">
                          ₹{formatCurrency(ps.civilExpenseLimit)} <span className="text-slate-300 mx-0.5">/</span> <span className="text-[#1e3a8a] font-bold">₹{formatCurrency(ps.civilNotApplicableAmount || 0)}</span>
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-bold text-slate-800">
                          ₹{formatCurrency(ps.civilMaterialCost || 0)}
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-bold text-slate-800">
                          ₹{formatCurrency(ps.civilStandardAmount || 0)}
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-bold text-slate-800">
                          ₹{formatCurrency(ps.civilNonStandardAmount || 0)}
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-bold text-slate-800">
                          ₹{formatCurrency(ps.civilOthersAmount || 0)}
                        </td>
                      </tr>
                      {/* Elect Row */}
                      <tr className="hover:bg-slate-100/50 transition-colors">
                        <td className="px-2.5 py-1.5 text-[12px] font-semibold text-slate-800 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            Elect
                          </div>
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-medium">
                          ₹{formatCurrency(ps.electricalTargetCost)} <span className="text-slate-300 mx-0.5">/</span> <span className="text-slate-800 font-bold">₹{formatCurrency(Math.max(0, (ps.electricalExpenses || 0) - (ps.electricalNonStandardAmount || 0)))}</span>
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-medium">
                          ₹{formatCurrency(ps.electricalExpenseLimit)} <span className="text-slate-300 mx-0.5">/</span> <span className="text-[#1e3a8a] font-bold">₹{formatCurrency(ps.electricalNotApplicableAmount || 0)}</span>
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-bold text-slate-800">
                          ₹{formatCurrency(ps.electricalMaterialCost || 0)}
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-bold text-slate-800">
                          ₹{formatCurrency(ps.electricalStandardAmount || 0)}
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-bold text-slate-800">
                          ₹{formatCurrency(ps.electricalNonStandardAmount || 0)}
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-bold text-slate-800">
                          ₹{formatCurrency(ps.electricalOthersAmount || 0)}
                        </td>
                      </tr>
                      {/* Mech Row */}
                      <tr className="hover:bg-slate-100/50 transition-colors">
                        <td className="px-2.5 py-1.5 text-[12px] font-semibold text-slate-800 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <div className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                            Mech
                          </div>
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-medium">
                          ₹{formatCurrency(ps.mechanicalTargetCost)} <span className="text-slate-300 mx-0.5">/</span> <span className="text-slate-800 font-bold">₹{formatCurrency(Math.max(0, (ps.mechanicalExpenses || 0) - (ps.mechanicalNonStandardAmount || 0)))}</span>
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-medium">
                          ₹{formatCurrency(ps.mechanicalExpenseLimit)} <span className="text-slate-300 mx-0.5">/</span> <span className="text-[#1e3a8a] font-bold">₹{formatCurrency(ps.mechanicalNotApplicableAmount || 0)}</span>
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-bold text-slate-800">
                          ₹{formatCurrency(ps.mechanicalMaterialCost || 0)}
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-bold text-slate-800">
                          ₹{formatCurrency(ps.mechanicalStandardAmount || 0)}
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-bold text-slate-800">
                          ₹{formatCurrency(ps.mechanicalNonStandardAmount || 0)}
                        </td>
                        <td className="px-2.5 py-1.5 text-[12px] text-right whitespace-nowrap font-bold text-slate-800">
                          ₹{formatCurrency(ps.mechanicalOthersAmount || 0)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Collection Progress */}
            <div className="mt-4 pt-3.5 border-t border-slate-100 flex flex-col gap-2">
              <div className="flex justify-between items-end">
                <div className="flex flex-col gap-0.5">
                  <span className="text-slate-500 text-[10px] font-bold uppercase tracking-widest leading-none">Collection Progress</span>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-[12px] font-bold text-[#1e3a8a]">₹{formatCurrency(ps.totalPaid)}</span>
                    <span className="text-slate-300 text-[10px]">/</span>
                    <span className="text-[10px] font-bold text-slate-500 tracking-tight">₹{formatCurrency(ps.contractValue + ps.nonStandardAmount)}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-0.5">
                  <span className="text-[13px] font-black text-[#1e3a8a]">{Math.min(100, Math.round((ps.totalPaid / (ps.contractValue + ps.nonStandardAmount || ps.contractValue)) * 100))}%</span>
                  <span className="text-[8px] font-bold text-slate-400 tracking-tighter uppercase">Received</span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden shadow-inner">
                <div
                  className="h-full bg-[#1e3a8a] transition-all duration-1000 shadow-[0_0_8px_rgba(30,58,138,0.3)]"
                  style={{ width: `${Math.min(100, Math.round((ps.totalPaid / (ps.contractValue + ps.nonStandardAmount || ps.contractValue)) * 100))}%` }}
                />
              </div>
              {ps.showNonStdWarning && (
                <div className="mt-1 bg-rose-50 border border-rose-200 rounded-lg p-1.5 flex items-center justify-between">
                  <span className="text-[10px] font-bold text-rose-700 tracking-tight">PO Complete - Need to pay Non-Std:</span>
                  <span className="text-[11px] font-black text-rose-600 animate-pulse">₹{formatCurrency(ps.remainingNonStd)}</span>
                </div>
              )}
              <p className="text-[9px] font-medium text-slate-400 tracking-tight italic mt-0.5">
                * Collection based on total received vs. PO Value + Non-Standard amount.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity Log */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-4 animate-in fade-in slide-in-from-right duration-700">
        <div className="flex items-center justify-between">
          <h3 className="text-[13px] font-semibold text-slate-800 tracking-tight ">Recent activity</h3>
          <Activity className="w-4 h-4 text-slate-300" />
        </div>

        <div className="flex flex-col gap-4">
          {activityRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-4 opacity-30">
              <Activity className="w-8 h-8 text-slate-300 mb-1" />
              <p className="text-[11px] font-semibold text-slate-400 capitalize tracking-tight ">No activity found</p>
            </div>
          ) : (
            activityRows.map((log, idx) => {
              const config = ACTIVITY_CONFIG[log.type] || { icon: Activity, color: 'text-slate-400', bg: 'bg-slate-50' };
              const Icon = config.icon;
              return (
                <div key={idx} className="flex gap-3 animate-in fade-in slide-in-from-left-2 duration-300">
                  <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center shrink-0 border border-slate-100/50`}>
                    <Icon className={`w-4 h-4 ${config.color}`} />
                  </div>
                  <div className="flex flex-col gap-1 min-w-0">
                    <p className="text-[13px] font-semibold text-slate-700 leading-tight break-words ">
                      {log.message}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-semibold text-slate-400  tracking-tight ">
                        {log.actorName || 'System'}
                      </span>
                      <span className="text-slate-200 text-[10px]">•</span>
                      <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-400  italic">
                        <Clock className="w-3 h-3" />
                        {new Date(log.at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {
        canManageFinance && (
          <>
            <ProjectTransactionHistoryModal
              isOpen={historyOpen}
              onClose={() => setHistoryOpen(false)}
              project={project}
              refreshTrigger={refreshTrigger}
              onRecordPayment={() => {
                setCreditOpen(true);
              }}
            />
            <RecordProjectCreditModal
              isOpen={creditOpen}
              onClose={() => setCreditOpen(false)}
              project={project}
              onSuccess={handleAfterMutation}
            />
            <RequestProjectExpenseModal
              isOpen={expenseOpen}
              onClose={() => setExpenseOpen(false)}
              project={project}
              onSuccess={handleAfterMutation}
            />
          </>
        )
      }

      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col gap-4 animate-in fade-in slide-in-from-right duration-700">
        <div className="flex items-center justify-between">
          <h3 className="text-[13px] font-black text-slate-800 tracking-tight">Project manager</h3>
          <User className="w-4 h-4 text-slate-300" />
        </div>

        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-slate-50 flex items-center justify-center text-[#2b448f] font-black text-lg border border-slate-200">
            {project?.projectManager ? getInitials(project.projectManager.name) : 'PM'}
          </div>

          <div className="flex flex-col flex-1 min-w-0">
            <span className="text-[9px] font-extrabold text-[#2b448f] tracking-widest mb-1 leading-none">Assigned PM</span>
            <h4 className="text-[14px] font-black text-slate-800 tracking-tight truncate leading-none mb-2">
              {project?.projectManager?.name || 'Not assigned'}
            </h4>
            <div className="flex flex-col gap-1.5 mt-1">
              <div className="flex items-center gap-2 group">
                <Mail className="w-3 h-3 text-slate-300 group-hover:text-[#2b448f] transition-colors" />
                <span className="text-[11px] font-bold text-slate-500 truncate lowercase">
                  {project?.projectManager?.email || 'No email provided'}
                </span>
              </div>
              <div className="flex items-center gap-2 group">
                <Phone className="w-3 h-3 text-slate-300 group-hover:text-[#2b448f] transition-colors" />
                <span className="text-[11px] font-bold text-slate-500">{project?.projectManager?.phone || 'No phone provided'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div >
  );
};
